import { useEffect, useMemo, useState } from 'react';
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
} from '@mui/material';
import { AccountEntryService, InvoiceService, JobsService } from 'api';
import {
  AccountEntry,
  Activity,
  EditAccountEntryBody,
} from 'api/accountEntries/accountEntries.types';
import { JobQueryParams } from 'api/jobs/jobs.types';
import { isSuccessful } from 'api/utils';
import { StyledButton } from 'components/Buttons';
import { StyledAutocomplete, StyledTextField } from 'components/Inputs';
import { LoaderFullScreen } from 'components/Loaders/LoaderFullScreen';
import { useAuth } from 'contexts/authContext';
import { isString } from 'lodash';
import { CorrectiveInvoice, Invoice } from 'pages/invoices/invoices.types';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  canEditInvoice,
  isInvoiceCorrective,
} from 'utils/invoiceFunctions.utils';
import { appToast } from 'utils/toast.utils';
import * as ST from '../../InvoiceAllocations.css';
import { JobsSubtable } from '../AddAllocationDialog/components/JobsSubtable';
import { JobsSubtableData } from '../AddAllocationDialog/types';
import * as S from './AllocationDetails.css';

type Props = {
  open: boolean;
  invoice: Invoice | CorrectiveInvoice;
  unallocatedAmount: number;
  activity?: Activity;
  allocationData: AccountEntry | undefined;
  accountEntriesLoading: boolean;
  onClose: () => void;
  onSubmit: () => void;
};

export const AllocationDetails = ({
  open,
  invoice,
  unallocatedAmount,
  allocationData,
  accountEntriesLoading,
  onClose,
  onSubmit,
}: Props) => {
  const { user, allowedReplacementsByCode } = useAuth();

  const [isEditing, setIsEditing] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobsSubtableData | null>(null);
  const [activitiesOptions, setActivitiesOptions] = useState<Activity[]>([]);
  const [jobsQueryParams, setJobsQueryParams] = useState<JobQueryParams>({
    filterText: '',
    pageNumber: 1,
    resultSize: 10,
    jobStatus: ['IN_PROGRESS', 'FINALIZED'],
  });

  const { t } = useTranslation(['invoice', 'misc', 'currency']);

  const formDefaultValues = useMemo(
    () => ({
      activity: allocationData?.activity,
      comment: allocationData?.comment,
      complaintReason: allocationData?.complaintReason,
      creationDate: allocationData?.creationDate,
      description: allocationData?.description,
      id: allocationData?.id,
      invoice: {
        __type: invoice.invoiceClass?.type.toLowerCase().includes('corrective')
          ? 'CorrectiveInvoice'
          : 'Invoice',
        id: invoice.id,
        invoiceClass: invoice.invoiceClass,
      },
      job: {
        id: allocationData?.job?.id,
      },
      invoiceAmount: allocationData?.invoiceAmount,
      jobAmount: allocationData?.jobAmount,
      lock: allocationData?.lock,
      rejectionAcknowledged: allocationData?.rejectionAcknowledged,
      status: allocationData?.status,
    }),
    [allocationData],
  );

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isValid },
  } = useForm<EditAccountEntryBody>({
    mode: 'onChange',
    defaultValues: formDefaultValues,
  });

  const { getJobs, loading: jobsLoading, data } = JobsService.useGetJobs();
  const {
    data: activitiesData,
    loading: activitiesLoading,
    getInvoiceActivities,
  } = InvoiceService.useGetInvoiceActivities();
  const { editAccountEntry, loading: editAccountEntryLoading } =
    AccountEntryService.useEditAccountEntry();
  const {
    acknowledgeRejectionAccountEntry,
    loading: acknowledgeRejectionLoading,
  } = AccountEntryService.useAcknowledgeRejectionAccountEntry();

  const isCorrective = useMemo(
    () => isInvoiceCorrective(invoice.invoiceClass),
    [invoice.invoiceClass],
  );

  const onFormSubmit = async (data: EditAccountEntryBody) => {
    const res = await editAccountEntry(allocationData?.id as number, data);
    isSuccessful(res) &&
      appToast.success(t('message.updateSuccess', { ns: 'misc' }));
    await onSubmit();
    onClose();
  };

  const handleAcknowledgeRejection = async () => {
    const res = await acknowledgeRejectionAccountEntry(
      allocationData?.id as number,
    );
    isSuccessful(res) &&
      appToast.success(t('rejectionAcknowledmentSuccess', { ns: 'invoice' }));
    await onSubmit();
    onClose();
  };

  const handleExitDialog = () => {
    setIsEditing(false);
    setSelectedJob(null);
    setJobsQueryParams({
      filterText: '',
      pageNumber: 1,
      resultSize: 10,
      jobStatus: ['IN_PROGRESS', 'FINALIZED'],
    });
    reset();
  };

  const isBackdropVisible: boolean =
    editAccountEntryLoading ||
    accountEntriesLoading ||
    acknowledgeRejectionLoading;

  useEffect(() => {
    if (!jobsQueryParams.filterText && allocationData?.description) {
      setValue('description', allocationData?.description);
    } else setValue('description', jobsQueryParams.filterText);
  }, [jobsQueryParams.filterText, allocationData?.description]);

  useEffect(() => {
    if (selectedJob) setValue('job.id', selectedJob?.id);
  }, [selectedJob?.id]);

  useEffect(() => {
    getJobs(jobsQueryParams);
  }, [getJobs, jobsQueryParams]);

  useEffect(() => {
    if (activitiesData?.length) {
      setActivitiesOptions((prev) => [...prev, ...activitiesData]);
    }
  }, [activitiesData]);

  useEffect(() => {
    getInvoiceActivities(invoice.id, isCorrective);
  }, [getInvoiceActivities, invoice.id, isCorrective]);

  // set default form values when allocation changes
  useEffect(() => {
    reset(formDefaultValues);
  }, [allocationData]);

  return (
    <ST.StyledDialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      align='flex-start'
      TransitionProps={{ onExited: handleExitDialog }}
    >
      <LoaderFullScreen loading={isBackdropVisible} />
      <DialogTitle>
        {`${t('allocation', { ns: 'invoice' })} - ${t('invoice', {
          ns: 'invoice',
        })} ${invoice.code}`}
      </DialogTitle>
      <DialogContent dividers>
        <form>
          <S.List>
            <S.ListRow>
              <S.ListRowTitle>
                {t('invoice', { ns: 'invoice' })}:
              </S.ListRowTitle>
              <S.ListRowData>
                {!isEditing ? (
                  invoice.code
                ) : (
                  <StyledTextField
                    id='invoice'
                    defaultValue={invoice.code}
                    size='small'
                    padding='dense'
                    disabled
                    width={230}
                  />
                )}
              </S.ListRowData>
            </S.ListRow>
            <S.ListRow>
              <S.ListRowTitle>
                {t('unallocatedAmount', { ns: 'invoice' })}:
              </S.ListRowTitle>
              <S.ListRowData>
                {!isEditing ? (
                  unallocatedAmount
                ) : (
                  <StyledTextField
                    id='unallocatedAmount'
                    defaultValue={unallocatedAmount}
                    size='small'
                    padding='dense'
                    disabled
                    width={230}
                  />
                )}
              </S.ListRowData>
            </S.ListRow>
            <S.ListRow>
              <S.ListRowTitle>
                {t('allocationAmount', { ns: 'invoice' })}:
              </S.ListRowTitle>
              <S.ListRowData>
                {!isEditing ? (
                  allocationData?.invoiceAmount
                ) : (
                  <StyledTextField
                    {...register('invoiceAmount', {
                      pattern: {
                        value: /^-?(?:\d+|\d{1,3}(?:,\d{3})+)(?:[\.|\,]\d+)?$/,
                        message: t('number', { ns: 'validation' }),
                      },
                      setValueAs: (v) =>
                        isString(v) ? v?.replace(',', '.') : v,
                    })}
                    id='invoiceAmount'
                    defaultValue={allocationData?.invoiceAmount}
                    size='small'
                    error={!!errors.invoiceAmount}
                    padding='dense'
                    width={230}
                  />
                )}
              </S.ListRowData>
              <S.ErrorContainer>
                {errors.invoiceAmount && (
                  <S.Error>{errors?.invoiceAmount?.message}</S.Error>
                )}
              </S.ErrorContainer>
            </S.ListRow>
            <S.ListRow>
              <S.ListRowTitle>
                {t('currency', { ns: 'currency' })}:
              </S.ListRowTitle>
              <S.ListRowData>
                {!isEditing ? (
                  invoice.currencyCode.currency
                ) : (
                  <StyledTextField
                    id='currency'
                    size='small'
                    padding='dense'
                    defaultValue={invoice.currencyCode.currency}
                    disabled
                    width={230}
                  />
                )}
              </S.ListRowData>
            </S.ListRow>
            <S.ListRow>
              <S.ListRowTitle>
                {t('allocationActivity', { ns: 'invoice' })}:
              </S.ListRowTitle>
              <S.ListRowData>
                {!isEditing ? (
                  allocationData?.activity?.code
                ) : (
                  <Controller
                    name='activity'
                    control={control}
                    rules={{ required: t('required', { ns: 'validation' }) }}
                    render={(props) => (
                      <StyledAutocomplete
                        {...props}
                        disablePortal
                        padding='dense'
                        getOptionLabel={(o) => (o as Activity).code}
                        options={activitiesOptions}
                        loading={activitiesLoading}
                        defaultValue={allocationData?.activity}
                        isOptionEqualToValue={(option, value) =>
                          (value as Activity).id === (option as Activity).id
                        }
                        loadingText={t('combobox.searching', { ns: 'misc' })}
                        noOptionsText={t('combobox.noMatches', { ns: 'misc' })}
                        onChange={(_, data) => props.field.onChange(data)}
                        renderInput={(params) => (
                          <StyledTextField
                            {...params}
                            size='small'
                            padding='dense'
                            error={!!errors.activity}
                            width={230}
                          />
                        )}
                      />
                    )}
                  />
                )}
              </S.ListRowData>
              <S.ErrorContainer>
                {errors.activity && (
                  <S.Error>{t('required', { ns: 'validation' })}</S.Error>
                )}
              </S.ErrorContainer>
            </S.ListRow>
            <S.ListRow>
              <S.ListRowTitle>
                {t('description', { ns: 'misc' })}:
              </S.ListRowTitle>
              <S.ListRowData>
                {!isEditing ? (
                  allocationData?.description
                ) : (
                  <StyledTextField
                    {...register('description', {
                      maxLength: {
                        value: 256,
                        message: t('maxLength', {
                          ns: 'validation',
                          replace: { '0': '256' },
                        }),
                      },
                    })}
                    id='description'
                    defaultValue={allocationData?.description}
                    size='small'
                    error={!!errors.description}
                    padding='dense'
                    width={230}
                  />
                )}
              </S.ListRowData>
              <S.ErrorContainer>
                {errors.description && (
                  <S.Error>
                    {errors?.description?.message ||
                      t('required', { ns: 'validation' })}
                  </S.Error>
                )}
              </S.ErrorContainer>
            </S.ListRow>
            {allocationData?.complaintReason && (
              <>
                <S.ListRow>
                  <S.ListRowTitle>
                    {allocationData?.status === 'REJECTED'
                      ? t('rejectionReason', { ns: 'invoice' })
                      : t('complaintReason', { ns: 'invoice' })}
                  </S.ListRowTitle>
                  <S.ListRowData>
                    {allocationData?.complaintReason}
                  </S.ListRowData>
                </S.ListRow>
                {!allocationData?.rejectionAcknowledged &&
                  allocationData?.status === 'REJECTED' && (
                    <S.ListRow>
                      <S.ListRowTitle>
                        {t('rejectionAcknowledgment', { ns: 'invoice' })}:
                      </S.ListRowTitle>
                      <S.ListRowData>
                        <div>
                          <S.StyledCheckbox
                            size='small'
                            disableRipple
                            onChange={handleAcknowledgeRejection}
                          />
                        </div>
                      </S.ListRowData>
                    </S.ListRow>
                  )}
              </>
            )}
          </S.List>
        </form>
        {isEditing && (
          <>
            <Divider />
            {data && (
              <JobsSubtable
                data={data.jobs}
                count={data.count}
                loading={jobsLoading}
                params={jobsQueryParams}
                selectedJob={selectedJob}
                setParams={setJobsQueryParams}
                onSelectJob={setSelectedJob}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {allocationData?.status === 'PENDING' &&
          canEditInvoice(invoice, user, allowedReplacementsByCode) &&
          (isEditing ? (
            <StyledButton
              disabled={!isValid}
              $primary
              type='submit'
              onClick={handleSubmit(onFormSubmit)}
            >
              {t('button.save', { ns: 'misc' })}
            </StyledButton>
          ) : (
            <StyledButton $primary onClick={() => setIsEditing(true)}>
              {t('button.edit', { ns: 'misc' })}
            </StyledButton>
          ))}

        <StyledButton onClick={onClose}>
          {t('button.cancel', { ns: 'misc' })}
        </StyledButton>
      </DialogActions>
    </ST.StyledDialog>
  );
};
